import { FC, useState } from 'react';
import { Formik, Form } from 'formik';
import { Box, Typography } from '@mui/material';

import CustomButton from '../CustomButton';
import CustomInput from '../CustomInput';
import { useAppDispatch } from '../../hooks/redux';
import { Contact, deleteContactFromList, updateEmergencyContactList } from '../../store/slices/customizeSlice';
import { useCreateContactMutation, useDeleteContactMutation, useUpdateContactMutation } from '../../store/services/userContactsApi';

import styles from './ContactModal.module.scss';

type FormikValues = {
  firstName?: string;
  lastName?: string;
  username?: string;
  relationship?: string;
  email?: string;
  phoneNumber?: string;
}

type ContactModalProps = {
  closeModal: () => void,
  targetId?: number | undefined | null,
  contacts?: Contact,
  type: string,
}

const ContactModal: FC<ContactModalProps> = ({
  closeModal,
  contacts,
  type,
  targetId,
}) => {
  const dispatch = useAppDispatch();
  const [ updateContact, {isSuccess: isSuccessUpdate} ] = useUpdateContactMutation();
  const [ createContact, {isSuccess: isSuccessCreate} ] = useCreateContactMutation();
  const [ deleteContact, {isSuccess: isSuccessDelete} ] = useDeleteContactMutation();

  const [resErrors, setResErrors] = useState<{attr: string | null, code: string, detail: string}[] | null>(null);

  const initialValues = {
    firstName: contacts?.first_name || '',
    lastName: contacts?.last_name || '',
    username: contacts?.username || '',
    relationship: contacts?.relationship || '',
    email: contacts?.email || '',
    phoneNumber: contacts?.phone_number || '',
  };

  const handleSubmit = async (values: FormikValues) => {
    try {
      if (targetId) {
        await updateContact({
          first_name: values.firstName,
          last_name: values.lastName,
          phone_number: values.phoneNumber,
          username: values.username,
          email: values.email,
          relationship: values.relationship,
          type,
          ...(targetId ? { id: targetId } : {}),
        });
        dispatch(updateEmergencyContactList({
          first_name: values.firstName,
          last_name: values.lastName,
          phone_number: values.phoneNumber,
          username: values.username,
          email: values.email,
          relationship: values.relationship,
          type,
          ...(targetId ? { id: targetId } : {}),
        }))
      } else {
        const { data } = await createContact({
          first_name: values.firstName,
          last_name: values.lastName,
          phone_number: values.phoneNumber,
          username: values.username,
          email: values.email,
          relationship: values.relationship,
          type,
        });

        dispatch(updateEmergencyContactList({
          ...data,
          type: type,
        }))
      }
      setResErrors(null);
      closeModal();
    } catch (error) {
      console.error(error);
      setResErrors((error as any)?.data?.errors || null);
    }
  };

  const handleDeleteContact = async () => {
    try {
      if (!targetId) {
        console.error("targetId is undefined!");
        return;
      }

      await deleteContact(targetId);
      dispatch(deleteContactFromList({ id: targetId }));
      setResErrors(null);
      closeModal();
    } catch (error) {
      console.error(error);
      setResErrors((error as any)?.data?.errors || null);
    }
  }

  const formikValidate = (values: FormikValues) => {
    const errors: FormikValues = {};
    if (!values.firstName) {
      errors.firstName = 'First name is required';
    }
    if (!values.lastName) {
      errors.lastName = 'Last name is required';
    }
    if (!values.relationship) {
      errors.relationship = 'Relationship is required';
    }
    if (!values.email) {
      errors.email = 'Email is required';
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
    ) {
      errors.email = 'Invalid email address';
    }
    if (!values.phoneNumber) {
      errors.phoneNumber = 'Phone Number is required';
    } else if (!/^\d{10}$/.test(values.phoneNumber)) {
      errors.phoneNumber = 'Phone Number must be 10 digits';
    }
    return errors;
  }

  return (
    <Box className={styles.accountInfo}>
      <Typography variant='h7Bold'>{`${ targetId ? `${type} contact` : `New ${type.toLowerCase()} contact`}`}</Typography>
      <Formik
        initialValues={initialValues}
        validate={formikValidate}
        onSubmit={handleSubmit}
      >
        {({ values, errors, handleChange, isSubmitting }) => (
          <Form className={styles.accountInfoForm}>

            <Box className={styles.nameInputs}>
              <CustomInput
                inputLabel='First Name'
                inputType='text'
                inputName="firstName"
                value={values?.firstName ?? contacts?.first_name}
                required
                error={errors.firstName}
                customClass={styles.customInput}
                onChange={handleChange}
              />
              <CustomInput
                inputLabel='Last Name'
                inputType='text'
                inputName="lastName"
                value={values?.lastName ?? contacts?.last_name}
                required
                error={errors.lastName}
                customClass={styles.customInput}
                onChange={handleChange}
              />
            </Box>
            <CustomInput
              inputType='text'
              inputName='username'
              inputLabel="Username (optional)"
              value={values?.username ?? contacts?.username}
              error={errors.username}
              customClass={styles.customInput}
              onChange={handleChange}
            />
            <CustomInput
              inputLabel='Relationship'
              inputType='text'
              inputName="relationship"
              value={values?.relationship ?? contacts?.relationship}
              required
              error={errors.relationship}
              customClass={styles.customInput}
              onChange={handleChange}
            />
            <CustomInput
              inputLabel='Email'
              inputType='text'
              inputName="email"
              value={values?.email ?? contacts?.email}
              required
              error={errors.email}
              customClass={styles.customInput}
              onChange={handleChange}
            />
            <CustomInput
              inputLabel='Phone Number'
              inputType='text'
              inputName="phoneNumber"
              value={values?.phoneNumber ?? contacts?.phone_number}
              required
              error={errors.phoneNumber}
              customClass={styles.customInput}
              onChange={handleChange}
            />

           {targetId ? (
            <Box className={styles.accountBtns}>
              <CustomButton
                btnType='button'
                variant='outlined'
                onClick={handleDeleteContact}
              >
                Delete contact
              </CustomButton>
              <CustomButton
                btnType='submit'
                customClass={styles.customButton}
                disabled={isSubmitting}
              >
                Save changes
              </CustomButton>
            </Box>
            ) : (
              <Box className={styles.accountBtns}>
                <CustomButton
                  btnType='button'
                  variant='outlined'
                  onClick={closeModal}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  btnType='submit'
                  customClass={styles.customButton}
                  disabled={isSubmitting}
                >
                  Save
                </CustomButton>
              </Box>
            )}
            {(isSuccessCreate || isSuccessUpdate || isSuccessDelete) && <Typography variant='h12' sx={{ color: '#208B64' }}>
              {`Contact ${isSuccessCreate ? 'created' : isSuccessDelete ? 'deleted' : 'updated'} successfully.`}
            </Typography>}
            {resErrors && resErrors.map((error, index) => (
              <Typography key={index} variant='h12' sx={{ color: '#C8102E' }}>
                {error.detail}
              </Typography>
            ))}
          </Form>)}
      </Formik>
    </Box>
  )
}

export default ContactModal;
